import { AlertCircleIcon, Attachment01Icon, File02Icon, Film01Icon, Image01Icon, MusicNote01Icon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatBytes } from '../../lib/format.js';
import { Skeleton } from '../primitives/states.js';

export type MediaKind = 'pdf' | 'image' | 'video' | 'audio' | 'file';

const KIND_ICON: Record<MediaKind, React.ReactNode> = {
  pdf: <Icon icon={File02Icon} aria-hidden />,
  image: <Icon icon={Image01Icon} aria-hidden />,
  video: <Icon icon={Film01Icon} aria-hidden />,
  audio: <Icon icon={MusicNote01Icon} aria-hidden />,
  file: <Icon icon={Attachment01Icon} aria-hidden />,
};

const KIND_LABEL: Record<MediaKind, string> = {
  pdf: 'PDF',
  image: 'Imagen',
  video: 'Video',
  audio: 'Audio',
  file: 'Archivo',
};

export interface MediaPreviewProps {
  kind: MediaKind;
  name: string;
  sizeBytes?: number | null;
  /** Optional thumbnail for images/videos (a URL the app already authorized). */
  thumbnailUrl?: string | null;
  status?: 'ready' | 'loading' | 'error';
  errorMessage?: string;
  /** Link to open/download (authorized URL). */
  href?: string;
  onOpen?: () => void;
  className?: string;
}

/** Attachment placeholder card: type icon, name, size, and loading / error states. */
export const MediaPreview = React.forwardRef<HTMLDivElement, MediaPreviewProps>(function MediaPreview(
  { kind, name, sizeBytes, thumbnailUrl, status = 'ready', errorMessage, href, onOpen, className },
  ref,
) {
  if (status === 'loading') {
    return (
      <div ref={ref} role="status" aria-label={`Cargando ${KIND_LABEL[kind].toLowerCase()}`} className={cn('flex w-64 max-w-full items-center gap-3 rounded-control border border-border-subtle bg-surface-default p-2.5', className)}>
        <Skeleton className="size-10 shrink-0" />
        <div className="grid flex-1 gap-1.5">
          <Skeleton className="h-3.5 w-3/4" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
    );
  }
  const error = status === 'error';
  const meta = [KIND_LABEL[kind], sizeBytes != null ? formatBytes(sizeBytes) : null].filter(Boolean).join(' · ');
  const inner = (
    <>
      {thumbnailUrl && !error && (kind === 'image' || kind === 'video') ? (
        <img src={thumbnailUrl} alt="" className="size-10 shrink-0 rounded-[6px] object-cover" />
      ) : (
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-[6px] [&_svg]:size-5',
            error ? 'bg-danger-bg text-danger' : 'bg-surface-subtle text-text-secondary',
          )}
        >
          {error ? <Icon icon={AlertCircleIcon} aria-hidden /> : KIND_ICON[kind]}
        </span>
      )}
      <span className="grid min-w-0 flex-1 text-left">
        <span className="truncate text-body font-medium text-text-primary">{name}</span>
        <span className={cn('truncate text-meta', error ? 'text-danger' : 'text-text-secondary')}>
          {error ? (errorMessage ?? 'No se pudo cargar el archivo') : meta}
        </span>
      </span>
    </>
  );
  const box = cn(
    'flex w-64 max-w-full items-center gap-3 rounded-control border bg-surface-default p-2.5',
    error ? 'border-danger' : 'border-border-subtle',
    (href || onOpen) && !error && 'hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    className,
  );
  if (href && !error) {
    return (
      <div ref={ref}>
        <a href={href} target="_blank" rel="noreferrer" className={box} aria-label={`Abrir ${name} (${meta})`}>
          {inner}
        </a>
      </div>
    );
  }
  if (onOpen && !error) {
    return (
      <div ref={ref}>
        <button type="button" onClick={onOpen} className={box} aria-label={`Abrir ${name} (${meta})`}>
          {inner}
        </button>
      </div>
    );
  }
  return (
    <div ref={ref} className={box} role={error ? 'alert' : undefined}>
      {inner}
    </div>
  );
});
